import Cookies from 'js-cookie';
import { toDateOnly } from './taskWorkflow';
import { isLegalCaseTask } from './permission';
import { actsAsOwner } from './ownership';

/**
 * Legal Court Case — the web side of a management task of kind `legal_case`
 * (backend: modules/task/legalCase.ts). The dropdown lists come from
 * GET /task/legal-options (legalCaseOptions.js); only the values the UI
 * branches on are named here.
 */

export const CaseStatus = { OPEN: 'Open', CLOSED: 'Closed' };
export const RiskLevel = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' };
export const ActionParty = { INTERNAL: 'Internal' };
export const FollowUpStatus = { PENDING: 'Pending', DELAYED: 'Delayed' };
export const DocumentStatus = { REQUIRED: 'Required', SUBMITTED: 'Submitted' };

// Same wording as the API, so a check made here reads like the 400 it saves.
export const CASE_TYPE_REQUIRED_MESSAGE = 'Case type is required';
export const COURT_REQUIRED_MESSAGE = 'Court / authority is required';
export const ACTION_USER_MESSAGE = 'Pick the internal person the action is pending from';
export const ESCALATION_REASON_MESSAGE = 'Say why the case needs escalation';
export const CLOSING_NOTE_MESSAGE = 'A closing note is required to close a case';
export const REOPEN_REASON_MESSAGE = 'A reason is required to reopen a closed case';
export const TARGET_REQUIRED_MESSAGE = 'Target date is required';
export const LINK_MESSAGE = 'Links must start with http:// or https://';

export const toOptions = (values) => (values || []).map((value) => ({ label: value, value }));

// A leading blank keeps an unset dropdown unset, instead of the select quietly
// taking its first option.
export const withBlank = (options, label = 'Select') => [{ label, value: '' }, ...options];

export const isHttpLink = (value) => !value || /^https?:\/\/\S+$/i.test(value);

const textOrNull = (value) => {
  const text = String(value ?? '').trim();
  return text ? text : null;
};

const amountOrNull = (value) =>
  value === '' || value === null || value === undefined ? null : Number(value);

/** Form values for LegalCaseFields: a stored case, or the defaults of a new one. */
export function legalCaseFormValues(legalCase) {
  const c = legalCase || {};
  return {
    caseType: c.caseType || '',
    court: c.court || '',
    caseNumber: c.caseNumber || '',
    opponent: c.opponent || '',
    advocate: c.advocate || '',
    filingDate: toDateOnly(c.filingDate),
    currentStage: c.currentStage || '',
    caseStatus: c.caseStatus || CaseStatus.OPEN,
    riskLevel: c.riskLevel || RiskLevel.MEDIUM,
    actionPendingFrom: c.actionPendingFrom || '',
    actionPendingUserId: c.actionPendingUserId ?? '',
    nextAction: c.nextAction || '',
    escalationNeeded: c.escalationNeeded === true,
    escalationReason: c.escalationReason || '',
    exposureAmount: c.exposureAmount ?? '',
    legalFeesPending: c.legalFeesPending ?? '',
    latestRemarks: c.latestRemarks || '',
    folderLink: c.folderLink || '',
    closingNote: c.closingNote || ''
  };
}

/**
 * The case body for POST /task (`legalCase`) and PUT /task/:id/legal. Follows
 * the backend's mergeLegalCase: the person goes only with Internal, and the
 * escalation reason only with an escalation.
 */
export function legalCasePayload(values) {
  const v = values || {};
  const internal = v.actionPendingFrom === ActionParty.INTERNAL;
  const escalation = v.escalationNeeded === true;
  const payload = {
    caseType: v.caseType || null,
    court: textOrNull(v.court),
    caseNumber: textOrNull(v.caseNumber),
    opponent: textOrNull(v.opponent),
    advocate: textOrNull(v.advocate),
    filingDate: v.filingDate || null,
    currentStage: textOrNull(v.currentStage),
    caseStatus: v.caseStatus || CaseStatus.OPEN,
    riskLevel: v.riskLevel || RiskLevel.MEDIUM,
    actionPendingFrom: v.actionPendingFrom || null,
    actionPendingUserId: internal && v.actionPendingUserId ? Number(v.actionPendingUserId) : null,
    nextAction: textOrNull(v.nextAction),
    escalationNeeded: escalation,
    escalationReason: escalation ? textOrNull(v.escalationReason) : null,
    exposureAmount: amountOrNull(v.exposureAmount),
    legalFeesPending: amountOrNull(v.legalFeesPending),
    latestRemarks: textOrNull(v.latestRemarks),
    folderLink: textOrNull(v.folderLink)
  };
  // The note field shows only for a Closed case; leaving it out otherwise keeps
  // the stored note when a case is edited or reopened.
  if (payload.caseStatus === CaseStatus.CLOSED) {
    payload.closingNote = textOrNull(v.closingNote);
  }
  return payload;
}

/** The backend's legalCaseStateError, checked before the round trip. */
export function legalCaseError(payload) {
  if (!payload.caseType) return CASE_TYPE_REQUIRED_MESSAGE;
  if (!payload.court) return COURT_REQUIRED_MESSAGE;
  if (payload.actionPendingFrom === ActionParty.INTERNAL && !payload.actionPendingUserId) {
    return ACTION_USER_MESSAGE;
  }
  if (payload.escalationNeeded && !payload.escalationReason) return ESCALATION_REASON_MESSAGE;
  if (payload.caseStatus === CaseStatus.CLOSED && !payload.closingNote) return CLOSING_NOTE_MESSAGE;
  if (!isHttpLink(payload.folderLink)) return LINK_MESSAGE;
  return null;
}

/**
 * A later target date for the same next action is a slip and needs a reason;
 * a new next action carries its own date. Same rule as the backend.
 */
export function targetDateChangeNeedsReason({ fromDate, toDate, fromAction, toAction }) {
  const from = toDateOnly(fromDate);
  const to = toDateOnly(toDate);
  if (!from || !to || to <= from) return false;
  return String(fromAction ?? '').trim() === String(toAction ?? '').trim();
}

/** Today as YYYY-MM-DD in local time. */
export function localToday(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** A Pending follow-up past its due date is Delayed, as the register shows it. */
export function effectiveFollowUpStatus(row, today = localToday()) {
  const status = row?.status || FollowUpStatus.PENDING;
  const due = toDateOnly(row?.dueDate);
  return status === FollowUpStatus.PENDING && due && due < today ? FollowUpStatus.DELAYED : status;
}

const signedInUserId = () => String(Cookies.get('userId') || '');
const isManagementUser = () =>
  String(Cookies.get('userVertical') || '').toLowerCase() === 'management';

/**
 * The owner or management may change the case. The API decides; this only
 * picks which buttons to show.
 */
export function canEditLegalCase(task) {
  if (!isLegalCaseTask(task)) return false;
  return actsAsOwner(task) || isManagementUser();
}

/** Hearings, follow-ups and documents are open to collaborators as well. */
export function canWriteLegalRecords(task) {
  if (canEditLegalCase(task)) return true;
  if (!isLegalCaseTask(task)) return false;
  const userId = signedInUserId();
  return (task.collaborators || []).some((row) => String(row.user_id ?? row.userId) === userId);
}

/** Whoever recorded a hearing, follow-up or document may also delete it. */
export function canDeleteLegalRecord(task, row) {
  return (
    canEditLegalCase(task) || (row?.createdBy != null && String(row.createdBy) === signedInUserId())
  );
}

// A case expense goes straight to Accounts, so it is never a draft: it is with
// Accounts until paid, or cancelled.
const CASE_EXPENSE_STATUS = {
  approved: { label: 'With Accounts', className: 'bg-amber-100 text-amber-800' },
  paid: { label: 'Paid', className: 'bg-green-100 text-green-700' },
  void: { label: 'Cancelled', className: 'bg-gray-200 text-gray-600' }
};

const isPaidCaseExpense = (row) => row?.paid === true || row?.ledgerStatus === 'paid';
const isCancelledCaseExpense = (row) => ['void', 'rejected'].includes(row?.ledgerStatus);

export function caseExpenseStatus(row) {
  if (isPaidCaseExpense(row)) return CASE_EXPENSE_STATUS.paid;
  if (isCancelledCaseExpense(row)) return CASE_EXPENSE_STATUS.void;
  return CASE_EXPENSE_STATUS.approved;
}

/** Rupees still with Accounts and already paid; cancelled lines count for neither. */
export function caseExpenseTotals(rows) {
  return (rows || []).reduce(
    (totals, row) => {
      const amount = Number(row?.amount) || 0;
      if (isPaidCaseExpense(row)) totals.paid += amount;
      else if (!isCancelledCaseExpense(row)) totals.withAccounts += amount;
      return totals;
    },
    { withAccounts: 0, paid: 0 }
  );
}

/** Only an unpaid line can come back out of the Accounts queue. */
export function canCancelCaseExpense(task, row) {
  return canEditLegalCase(task) && !isPaidCaseExpense(row) && !isCancelledCaseExpense(row);
}

export function caseStatusChipClass(status) {
  switch (status) {
    case 'Awaiting Hearing':
      return 'bg-purple-100 text-purple-700';
    case 'Documents Pending':
      return 'bg-amber-100 text-amber-800';
    case 'Under Review':
      return 'bg-indigo-100 text-indigo-700';
    case 'On Hold':
      return 'bg-gray-200 text-gray-700';
    case CaseStatus.CLOSED:
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-blue-100 text-blue-700';
  }
}

export function riskChipClass(level) {
  if (level === RiskLevel.HIGH) return 'bg-rose-50 text-rose-700';
  if (level === RiskLevel.LOW) return 'bg-green-100 text-green-700';
  return 'bg-amber-100 text-amber-800';
}

// Same limit as the API's LEGAL_NOTE_MAX_LENGTH.
export const NOTE_MAX_LENGTH = 2000;

// `payload.entity` on a legal_case history row says what the entry is about.
const LOG_ENTITY_LABELS = {
  note: 'Note',
  case: 'Case',
  hearing: 'Hearing',
  followUp: 'Follow-up',
  document: 'Document',
  weeklyReview: 'Weekly review',
  comment: 'Comment',
  expense: 'Expense'
};

const LOG_TYPE_LABELS = {
  due_date_change: 'Target date',
  owner_transfer: 'Owner transfer',
  budget: 'Budget',
  spend: 'Spend',
  review: 'Review',
  assign: 'Assign',
  permission: 'Permission / Compliance',
  subtask_delete: 'Subtask deleted'
};

export const isLogNote = (row) => row?.type === 'legal_case' && row?.payload?.entity === 'note';

export const isLogComment = (row) =>
  row?.type === 'legal_case' && row?.payload?.entity === 'comment';

const byCreatedAt = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

/**
 * The log as threads: entries newest first, each carrying its comments oldest
 * first. A comment whose entry is not in the list shows as an entry itself.
 */
export function logThreads(history) {
  const rows = history || [];
  const ids = new Set(rows.map((row) => row.id));
  const commentsByEntry = new Map();
  const entries = [];
  for (const row of rows) {
    const parentId = isLogComment(row) ? row.payload.parentId : null;
    if (parentId && ids.has(parentId)) {
      if (!commentsByEntry.has(parentId)) commentsByEntry.set(parentId, []);
      commentsByEntry.get(parentId).push(row);
    } else {
      entries.push(row);
    }
  }
  return entries
    .sort((a, b) => byCreatedAt(b, a))
    .map((row) => ({ ...row, comments: (commentsByEntry.get(row.id) || []).sort(byCreatedAt) }));
}

/** The chip on a court-case log entry, from its history row. */
export function logEntryLabel(row) {
  if (row?.type === 'legal_case') {
    if (row.payload?.entity === 'case' && row.payload?.action === 'target') return 'Target date';
    return LOG_ENTITY_LABELS[row.payload?.entity] || 'Case';
  }
  return LOG_TYPE_LABELS[row?.type] || 'Update';
}

/** "2026-09-20" → "20 Sept 2026"; '---' when there is no date. */
export function formatDay(value) {
  const day = toDateOnly(value);
  if (!day) return '---';
  return new Date(`${day}T00:00:00`).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

/** "Reopened 12 Sept 2026 by Gaurav Verma" for the API's `legalCase.reopened`. */
export function reopenedLabel(reopened) {
  if (!reopened) return '';
  const on = reopened.at ? ` ${formatDay(reopened.at)}` : '';
  const by = reopened.by ? ` by ${reopened.by}` : '';
  return `Reopened${on}${by}`;
}
